import { DivOverlay } from "common/Map/DivOverlay";
import { ZoomLevelConsumer } from "common/Map/mapUtils/ZoomLevelConsumer";
import { withMetricUnits } from "common/units/MetricUnitsContext";
import { getConsole } from "lib/getConsole";
import { constants as C } from "lib/units";
import _ from "lodash";
import React from "react";
import styles from "./DistanceMarkers.scss";
const console = getConsole("map", "DistanceMarkers");

const getLevelForZoom = (zoom, metricUnits) => {
  if (metricUnits) {
    if (zoom >= 15) {
      return 0;
    }
    if (zoom >= 13) {
      return 1;
    }
    if (zoom >= 12) {
      return 2;
    }
    if (zoom >= 10) {
      return 3;
    }
    return -1;
  } else {
    // todo test these threshold values
    if (zoom >= 14) {
      return 0;
    }
    if (zoom >= 13) {
      return 1;
    }
    if (zoom >= 11) {
      return 2;
    }
    if (zoom >= 9) {
      return 3;
    }
    return -1;
  }
};

// This component has recieved a lot of performance optimization, since
// distance markers are expensive to compute and render in a naive way
// while following functional programming paradigms.
//
// Here's the current optimization state of the component
// - marker generation has been optimized to be roughly O(log n) instead of O(n)
// - marker generation is cached such that it only is performed once per zoom level
// - the props that MarkerLevel receives are set up so that it will only rerender
//   when the *marker level* changes, not the zoom level.
//
// Right now, the biggest perf issue is that in thousand-plus mile routes,
// we end up rendering thousands of markers. This causes two problems:
// - (1) when each marker is added to the map, it causes a layout reflow when added
//   to the DOM that takes about 2ms. Multiply by a thousand, and you get a
//   two second hiccup when transitioning between markerLevel 1 and markerLevel 0
// - (2) when the map is panned, we update the positions of all thousand markers,
//   even the ones that are outside the map bounding box.
//
// The most efficient way to solve both of these is to ensure that only *visible*
// markers render. This is going to involve extending the DivOverlay for both
// gmaps and leaflet to support bounds-based virtualization.
// example: https://github.com/Leaflet/Leaflet/issues/1324#issuecomment-384697787
//
// It's also possible that there are some CSS changes that can be made to the
// DistanceMarker to help optimize repaint/reflow time, but I don't think those
// will net us very big wins compared to the virtualization thing.
class _DistanceMarkers extends React.PureComponent {
  componentDidMount() {
    // prime the cache in a requestIdleCallback in browsers that support it
    window.requestIdleCallback &&
      window.requestIdleCallback(() => {
        const { trackPoints, metricUnits } = this.props;
        console.log("priming marker cache in idle callback");
        getMarkers(trackPoints, metricUnits, 0);
        getMarkers(trackPoints, metricUnits, 1);
        getMarkers(trackPoints, metricUnits, 2);
        getMarkers(trackPoints, metricUnits, 3);
      });
  }

  render() {
    const { trackPoints, metricUnits } = this.props;

    return (
      <ZoomLevelConsumer>
        {({ zoomLevel, nextZoomLevel }) => (
          <MarkerLevel
            trackPoints={trackPoints}
            metricUnits={metricUnits}
            level={
              nextZoomLevel && getLevelForZoom(nextZoomLevel) > getLevelForZoom(zoomLevel)
                ? -1
                : getLevelForZoom(zoomLevel)
            }
          />
        )}
      </ZoomLevelConsumer>
    );
  }
}

export const DistanceMarkers = withMetricUnits(_DistanceMarkers);

// Using PureComponent here is actually better at preventing re-renders than
// a function component. ¯\_(ツ)_/¯
const MARKER_OFFSET = [0, -8];
class MarkerLevel extends React.PureComponent {
  render() {
    const { trackPoints, level, metricUnits } = this.props;
    if (level === -1) {
      return null;
    } // -1 means markers should not be visible at this zoom level
    const markers = getMarkers(trackPoints, metricUnits, level);

    return markers.map((m, index) => (
      <DivOverlay animatePosition={false} key={m.distance} position={m} offset={MARKER_OFFSET} pane="markerPane">
        <div className={styles.DistanceMarker}>
          <span>{m.label}</span>
        </div>
      </DivOverlay>
    ));
  }
}

// there are 4 levels of distance marker:
// level 0 shows markers every $UNIT, where $UNIT is either 1mi or 1km
// level 1 shows markers every 2 * $UNIT
// level 2 shows markers every 5 * $UNIT
// level 3 shows markers every 25 * $UNIT
// beyond level 3, no distance markers are shown
let iterationCount = 0;
const LEVEL_INCREMENTS = [1, 2, 5, 25];
const _getMarkers = (trackPoints, metricUnits, markerLevel) => {
  const unitMeters = metricUnits ? C.METERS_IN_KM : C.METERS_IN_MILE;
  const unitInterval = LEVEL_INCREMENTS[markerLevel];
  const increment = unitMeters * unitInterval;
  const last = trackPoints[trackPoints.length - 1].distance;
  const markers = [];
  iterationCount = 0;

  const targetDist = (n) => increment * n;
  let lastIndex = 0;
  let markerNumber = Math.max(Math.ceil(trackPoints[0].distance / increment), 1);
  while (targetDist(markerNumber) < last) {
    const { index } = binarySearchNearest(targetDist(markerNumber), lastIndex, trackPoints.length, trackPoints);

    let p1, p2;
    if (trackPoints[index].distance > targetDist(markerNumber)) {
      lastIndex = index;
      p1 = trackPoints[Math.max(index - 1, 0)];
      p2 = trackPoints[index];
    } else {
      lastIndex = Math.min(index + 1, trackPoints.length - 1);
      p1 = trackPoints[index];
      p2 = trackPoints[Math.min(index + 1, trackPoints.length - 1)];
    }

    const distDelta = p2.distance - p1.distance;
    // peg percentage to 50% interpolation if we have a zeroish distance delta,
    // or if one of the points is missing a distance value (NaN)
    const percentage =
      Number.isFinite(distDelta) && distDelta > 0.0001 ? (targetDist(markerNumber) - p1.distance) / distDelta : 0.5;
    // at the distance figures we're working with, a simple linear interpolation
    // is sufficiently precise. No need to factor in the curvature of the earth :)
    const lat = p1.lat + percentage * (p2.lat - p1.lat);
    const lng = p1.lng + percentage * (p2.lng - p1.lng);
    markers.push({
      lat,
      lng,
      distance: increment * markerNumber,
      label: markerNumber * unitInterval,
    });
    markerNumber = markerNumber + 1;
  }
  console.log(
    `${markers.length} markers generated in ${iterationCount} iterations for a ${trackPoints.length}-point dataset`
  );
  return markers;
};
const cacheKey = (tps, metricUnits, markerLevel) =>
  `${tps.length}-${tps[tps.length - 1].distance}-${metricUnits}-${markerLevel}`;
const getMarkers = _.memoize(_getMarkers, cacheKey);

// return an {index, distance} corresponding to the nearest value to `target`
// in the provided trackpoints array
export const binarySearchNearest = (targetDist, sliceStart, sliceEnd, trackPoints) => {
  iterationCount++;
  if (sliceStart > sliceEnd) {
    // bail out in dev, this is a coding error
    if (process.env.NODE_ENV !== "production") {
      throw Error("sliceStart should never be greater than sliceEnd");
    }
    // try to keep going in prod
    return { index: -1, distance: Infinity };
  }
  if (sliceStart === sliceEnd) {
    return { index: -1, distance: Infinity };
  }
  if (sliceEnd - sliceStart === 1) {
    return { index: sliceStart, delta: Math.abs(trackPoints[0] - targetDist) };
  }
  if (sliceEnd - sliceStart === 2) {
    const left = {
      index: sliceStart,
      delta: Math.abs(trackPoints[sliceStart].distance - targetDist),
    };
    const right = {
      index: sliceEnd - 1,
      delta: Math.abs(trackPoints[sliceEnd - 1].distance - targetDist),
    };
    return left.delta < right.delta ? left : right;
  }
  const pivot = sliceStart + Math.floor((sliceEnd - sliceStart) / 2);
  const pivotDist = trackPoints[pivot].distance;
  return targetDist < pivotDist
    ? binarySearchNearest(targetDist, sliceStart, pivot, trackPoints)
    : binarySearchNearest(targetDist, pivot, sliceEnd, trackPoints);
};
